import { css } from '@emotion/core'
import {
  Button,
  ExternalStyles,
  FormControl,
  FormControlProps,
  HFlow,
  HFlowProps,
  Icon,
  Theme,
  Tooltip,
  useTheme,
  VFlow,
  VFlowProps,
} from 'bold-ui'
import { confirm } from 'components/modals/confirm'
import { ReactNode, useMemo } from 'react'

import { ErrorField, RadioField, RadioFieldProps, usePecField } from '../core'
import { FieldStatusContainer } from '../core/components/FieldStatus/FieldStatusContainer'

export type RadioOption<T extends string | number | string[]> = {
  value: T
  label: ReactNode
  disabled?: boolean
  tooltipText?: string
}

type RadioGroupValue = string | number | string[]

export interface RadioGroupFieldProps<T extends RadioGroupValue>
  extends Omit<FormControlProps, 'clearable'>,
    Pick<RadioFieldProps, 'name' | 'onChange' | 'onBlur' | 'onFocus' | 'disabled' | 'clearable' | 'clearFieldLabel'>,
    Pick<HFlowProps, 'hSpacing'>,
    Pick<VFlowProps, 'vSpacing'> {
  options: RadioOption<T>[]
  lines?: number
  style?: {
    hFlow?: ExternalStyles
    vFlow?: ExternalStyles
    radioField?: ExternalStyles
  }
  showErrorField?: boolean
  controlModifiedStatus?: boolean
  globalClearable?: boolean
}

export function RadioGroupField<T extends string | number | string[]>(props: Readonly<RadioGroupFieldProps<T>>) {
  const {
    name,
    options,
    lines = 1,
    vSpacing = 0.8,
    hSpacing,
    clearable,
    onChange,
    onBlur,
    onFocus,
    style,
    disabled,
    showErrorField = true,
    controlModifiedStatus = false,
    clearFieldLabel,
    globalClearable = false,
    ...rest
  } = props

  const cols = useRadioGroupColumns(options, lines)
  const theme = useTheme()
  const classes = createStyles(theme)

  const { input } = usePecField({
    name,
    subscription: { value: true },
  })

  const hasValue = !!input.value

  const handleGlobalClear = async () => {
    const confirmado =
      !controlModifiedStatus ||
      (await confirm({
        title: `Deseja excluir o campo ${clearFieldLabel}?`,
        type: 'danger',
        confirmLabel: 'Excluir',
      })())
    if (confirmado) input.onChange(undefined)
  }

  return (
    <FieldStatusContainer controlModifiedStatus={controlModifiedStatus} name={name}>
      <FormControl {...rest}>
        <VFlow vSpacing={0.5}>
          <HFlow hSpacing={hSpacing} style={[classes.row, style?.hFlow]}>
            {cols.map((col, index) => (
              <VFlow vSpacing={vSpacing} key={index} style={style?.vFlow}>
                {col.map(({ value, label, disabled: optionDisabled, tooltipText }) => (
                  <Tooltip key={value.toString()} text={tooltipText}>
                    <span>
                      <RadioField
                        key={value.toString()}
                        name={name}
                        value={value}
                        label={label}
                        clearable={clearable}
                        showClearConfirm={controlModifiedStatus}
                        clearFieldLabel={clearFieldLabel}
                        onChange={onChange}
                        onBlur={onBlur}
                        onFocus={onFocus}
                        style={style?.radioField}
                        disabled={!!optionDisabled || !!disabled}
                      />
                    </span>
                  </Tooltip>
                ))}
              </VFlow>
            ))}

            {globalClearable && (
              <Button
                size='small'
                skin='ghost'
                style={classes.globalClearButton}
                onClick={handleGlobalClear}
                disabled={!hasValue}
                title='Limpar seleção'
              >
                <Icon icon='timesDefault' fill='secondary' />
              </Button>
            )}
          </HFlow>
          {showErrorField && <ErrorField name={name} />}
        </VFlow>
      </FormControl>
    </FieldStatusContainer>
  )
}

export function useRadioGroupColumns<T extends string | number | string[]>(options: RadioOption<T>[], lines?: number) {
  return useMemo(() => {
    const nCols = Math.ceil(options.length / lines)

    return options.reduce((acc, curr, i) => {
      const colIdx = i % nCols
      if (!acc[colIdx]) acc[colIdx] = []
      acc[colIdx][Math.floor(i / nCols)] = curr

      return acc
    }, [])
  }, [lines, options])
}

const createStyles = (theme: Theme) => ({
  row: css`
    margin-left: -0.25rem;
    padding-top: 0.3rem;
  `,
  globalClearButton: css`
    padding: 0;
    margin: -1px 0;
    align-self: flex-start;
    &:hover {
      svg {
        fill: ${theme.pallete.status.danger.main};
      }
    }
  `,
})
